import React, { useState } from 'react';
import { Trash2, AlertTriangle, CheckCircle, Loader2, Search } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../services/api';

const PurgePage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handlePurge = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await api.post('/admin/purge-account', { email });
      setResult(res.data);
      setShowConfirm(false);
      setEmail('');
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors de la purge');
      setShowConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="purge-page">
      <div className="bg-[#1A1A1D] p-6 rounded-lg border border-[#3A3A3D]">
        <h2 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          <Trash2 className="h-5 w-5 text-[#FF3B5C]" />
          Purge de compte
        </h2>
        <p className="text-[#B8B8BA] text-sm mb-6">
          Supprime toutes les données associées à un email : compte, abonnements, transactions, identifiants IPTV, tickets, etc.
          L'email pourra ensuite être réutilisé pour créer un nouveau compte.
        </p>

        {error && (
          <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-[#FF3B5C] flex-shrink-0" />
            <span className="text-[#FF3B5C] text-sm">{error}</span>
          </div>
        )}

        {result && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 mb-4"
          >
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="h-5 w-5 text-[#00FF88]" />
              <span className="text-[#00FF88] font-bold">{result.message}</span>
            </div>
            {result.details && Object.keys(result.details).length > 0 && (
              <div className="mt-2 space-y-1">
                {Object.entries(result.details).map(([col, count]) => (
                  <div key={col} className="text-[#B8B8BA] text-xs flex justify-between max-w-sm">
                    <span className="font-mono">{col}</span>
                    <span className="text-[#FF6B35]">{count} supprimé(s)</span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        <div className="flex gap-3 items-end">
          <div className="flex-1 max-w-md">
            <label className="text-[#B8B8BA] text-xs mb-1 block">Email du compte à purger</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B6B6E]" />
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="exemple@email.com"
                className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-10 pr-4 py-3 text-white focus:border-[#FF3B5C] focus:outline-none"
                data-testid="purge-email-input"
              />
            </div>
          </div>
          <button
            onClick={() => { setShowConfirm(true); setResult(null); setError(''); }}
            disabled={!email || loading}
            className="px-6 py-3 bg-[#FF3B5C] hover:bg-[#FF3B5C]/80 text-white font-bold rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
            data-testid="purge-submit-btn"
          >
            <Trash2 className="h-4 w-4" />
            Purger
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowConfirm(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={e => e.stopPropagation()}
            className="bg-[#1A1A1D] p-6 rounded-lg border border-[#FF3B5C]/50 max-w-md w-full mx-4"
            data-testid="purge-confirm-modal"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-[#FF3B5C]/20 rounded-lg">
                <AlertTriangle className="h-6 w-6 text-[#FF3B5C]" />
              </div>
              <h3 className="text-white font-bold text-lg">Confirmer la suppression</h3>
            </div>
            <p className="text-[#B8B8BA] text-sm mb-2">
              Vous allez supprimer <strong>toutes les données</strong> associées à :
            </p>
            <p className="text-[#FF6B35] font-mono bg-[#222226] px-3 py-2 rounded mb-4">{email}</p>
            <p className="text-[#FF3B5C] text-xs mb-6">
              Cette action est irréversible. Le compte, les abonnements, transactions, identifiants IPTV, tickets et toutes les données liées seront définitivement supprimés.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2 bg-[#222226] border border-[#3A3A3D] text-[#B8B8BA] rounded-lg hover:bg-[#2A2A2D] transition-colors"
                data-testid="purge-cancel-btn"
              >
                Annuler
              </button>
              <button
                onClick={handlePurge}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-[#FF3B5C] text-white font-bold rounded-lg hover:bg-[#FF3B5C]/80 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="purge-confirm-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Supprimer définitivement
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PurgePage;
